import React from 'react';
import { BaseModal } from './BaseModal';

interface DarkModeConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DarkModeConsentModal({ isOpen, onClose }: DarkModeConsentModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Dark Mode & Cookie Consent"
    >
      <div className="space-y-6 text-gray-600 dark:text-gray-300">
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Cookie-Based Theme Preference
          </h3>
          <p className="mb-4">
            While dark mode preferences are typically stored in local storage without 
            requiring consent, this implementation demonstrates how OneTrust can be 
            integrated with functionality that depends on cookie consent.
          </p>
          <p className="mb-4">
            In this example:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Dark mode preference is stored in a performance cookie</li>
            <li>Theme switching requires consent for performance cookies</li>
            <li>If consent is withdrawn, the site reverts to light mode on reload</li>
            <li>Theme preference is tracked through OneTrust's consent management</li>
          </ul>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            How It Works
          </h3>
          <p className="mb-4">
            When you click the dark mode toggle:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-4">
            <li>The system checks for performance cookie consent</li>
            <li>If consent is granted, the theme is toggled and saved</li>
            <li>If consent is not granted, the OneTrust preference center opens</li>
            <li>Theme preference is preserved only with active consent</li>
          </ol>
        </div>

        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            This implementation showcases how OneTrust can be integrated with website functionality 
            while maintaining compliance with privacy regulations. In a real-world application, theme 
            preferences might be handled differently to avoid requiring cookie consent.
          </p>
        </div>
      </div>
    </BaseModal>
  );
}